import { blueGrey } from "@mui/material/colors";
import {
  BulletedListIcon,
  CardsThreeIcon,
  CreditCardIcon,
  DocumentIcon,
  GKButton,
  Grid2,
  Typography,
  UserCheckIcon,
  UserRectangleIcon,
} from "components";
import { RightArrowIcon } from "Pages/Website/Icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import useDocumentTitle from "utils/useDocumentTitle";

const cardDetails = [
  {
    title: "Personal Details",
    description: "Basic information to set up your profile.",
    icon: UserRectangleIcon,
  },
  {
    title: "Risk Profile",
    description: "Find your risk appetite for your goals.",
    icon: CardsThreeIcon,
  },
  {
    title: "Plans",
    description: "Choose your preferred wealth management plan.",
    icon: UserCheckIcon,
  },
  {
    title: "Agreement",
    description:
      "Review the auto-generated agreement before your client signs.",
    icon: BulletedListIcon,
  },
  {
    title: "Payment",
    description: "Complete payment to activate your account.",
    icon: DocumentIcon,
  },
  {
    title: "Review Your Details",
    description: "Review and confirm all the information you’ve provided.",
    icon: CreditCardIcon,
  },
];

const InvestorOnboarding = () => {
  useDocumentTitle("Investor / Get Started");
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const logo = location.search.split("logo=")[1];
  const token = searchParams.get("token");
  const clientName = searchParams.get("client_name");

  return (
    <Grid2 container bgcolor={"#fff"} height={"100vh"} alignItems={"center"}>
      <Grid2
        container
        spacing={{ md: 6, xs: 2 }}
        p={{ xs: 2, md: 5 }}
        size={{ xs: 12, md: 6 }}
      >
        <Grid2 size={12}>
          <img
            src={logo || "/Logos/iconFullLogo.png"}
            alt=""
            height={`-webkit-fill-available`}
            width={"auto"}
            style={{ maxHeight: "32px" }}
            onError={({ currentTarget }) => {
              currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
            }}
          />
        </Grid2>
        <Grid2 size={12} display={"flex"} flexDirection={"column"} gap={1}>
          <Typography
            fontWeight={700}
            bgcolor={"var(--primary-100)"}
            color={"var(--primary-900)"}
            width={"max-content"}
            px={2}
            py={1}
            borderRadius={2}
            fontSize={{ md: 24 }}
          >
            Welcome, {clientName || "Investor"}!
          </Typography>
          <Typography
            fontWeight={600}
            sx={{ fontSize: "clamp(1.375rem, 0.5938rem + 2.5vw, 2rem)" }}
          >
            Your Investment journey begins here
          </Typography>
          <Typography
            fontWeight={500}
            color={blueGrey[400]}
            sx={{ fontSize: "clamp(1rem, 0.6875rem + 1vw, 1.25rem)" }}
          >
            Click <span style={{ color: blueGrey[600] }}>Get started</span> and
            complete your onboarding in no time!
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <GKButton
            variant="contained"
            size="large"
            endIcon={<RightArrowIcon />}
            onClick={() => {
              navigate(`/investor/onboarding${location.search}`);
              axiosInstance.interceptors.request.use((config) => {
                config.headers.Authorization = `Token ${token}`;
                config.headers["x-gridkey-user-role"] = "client";
                return config;
              });
            }}
          >
            Get Started
          </GKButton>
        </Grid2>
      </Grid2>
      <Grid2
        container
        spacing={2}
        p={{ xs: 2, md: 5 }}
        size={{ xs: 12, md: 6 }}
        bgcolor={"var(--grey-bg-100)"}
        height={"100%"}
        alignContent={"center"}
      >
        <Grid2 size={12}>
          <Typography
            fontWeight={600}
            fontSize={10}
            color="var(--neutral-500)"
            textTransform={"uppercase"}
          >
            Just a few quick steps:
          </Typography>
        </Grid2>
        <Grid2 container size={12} spacing={0}>
          {cardDetails.map((dataItem, index) => (
            <Grid2
              key={index}
              size={{ xs: 12 }}
              container
              spacing={2}
              alignItems={"center"}
              borderBottom={"solid 1px var(--grey-100)"}
              py={2}
            >
              <Grid2 size={"auto"}>
                <Typography
                  height={28}
                  width={28}
                  bgcolor={"var(--grey-50)"}
                  borderRadius={10}
                  display={"flex"}
                  alignItems="center"
                  justifyContent={"center"}
                  fontWeight={600}
                  fontSize={10}
                >
                  {index + 1}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: "auto" }} container spacing={0}>
                <Grid2 size={12}>
                  <Typography
                    fontWeight={600}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                    sx={{
                      fontSize: "clamp(0.875rem, 0.7188rem + 0.5vw, 1rem)",
                    }}
                  >
                    {dataItem?.title}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography fontSize={16} fontWeight={400} color="#697586">
                    {dataItem?.description}
                  </Typography>
                </Grid2>
              </Grid2>
            </Grid2>
          ))}
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default InvestorOnboarding;
